/**
 * Engage globalAdmin side-by-side: live beer vs proposed shadow profile (ITT-970).
 */

import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../utils/firebase';
import { CALLABLE_BEER_RECOMPARE_BEER_PROFILE } from '../../utils/callableBeerConstants';
import useLatestBeerProfileVersion from '../../hooks/useLatestBeerProfileVersion';
import {
  BEER_PROFILE_COMPARISON_FIELDS,
  beerProfileOutcomeColor,
  beerProfileOutcomeLabel,
  formatBeerProfileFieldSource,
  formatBeerProfileFieldValue,
  normalizeBeerProfileConflicts,
} from '../../utils/beer/beerProfileComparison';

function ComparisonFieldRow({ field, fieldEntry, fieldSource }) {
  const outcome = fieldEntry?.outcome;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '120px 1fr 1fr 1fr' },
        gap: 1,
        alignItems: 'start',
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack spacing={0.5}>
        <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
          {field}
        </Typography>
        <Chip
          size="small"
          label={beerProfileOutcomeLabel(outcome)}
          color={beerProfileOutcomeColor(outcome)}
          variant={outcome === 'unavailable' ? 'outlined' : 'filled'}
        />
      </Stack>
      <Box>
        <Typography variant="caption" color="text.secondary" component="p">
          Live value
        </Typography>
        <Typography variant="body2">
          {formatBeerProfileFieldValue(fieldEntry?.current, field)}
        </Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" component="p">
          Proposed value
        </Typography>
        <Typography variant="body2">
          {outcome === 'conflicted'
            ? '—'
            : formatBeerProfileFieldValue(fieldEntry?.proposed, field)}
        </Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" component="p">
          Evidence
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
          {formatBeerProfileFieldSource(fieldSource)}
        </Typography>
      </Box>
    </Box>
  );
}

ComparisonFieldRow.propTypes = {
  field: PropTypes.string.isRequired,
  fieldEntry: PropTypes.shape({
    outcome: PropTypes.string,
    current: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    proposed: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  fieldSource: PropTypes.shape({
    kind: PropTypes.string,
    id: PropTypes.string,
  }),
};

ComparisonFieldRow.defaultProps = {
  fieldEntry: null,
  fieldSource: null,
};

function ConflictsList({ conflicts }) {
  if (conflicts.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No conflicts recorded on this version.
      </Typography>
    );
  }
  return (
    <Stack spacing={1}>
      {conflicts.map((conflict) => (
        <Alert
          key={`${conflict.field}-${conflict.reason}-${conflict.overrideId || 'none'}`}
          severity="warning"
          variant="outlined"
        >
          <Typography variant="body2">
            {conflict.field}: {conflict.reason}
          </Typography>
          <Typography variant="caption" component="p" sx={{ mt: 0.5 }}>
            Observations: {conflict.observationIds.join(', ') || '—'}
          </Typography>
          {conflict.overrideId ? (
            <Typography variant="caption" component="p">
              Override: {conflict.overrideId}
            </Typography>
          ) : null}
        </Alert>
      ))}
    </Stack>
  );
}

ConflictsList.propTypes = {
  conflicts: PropTypes.arrayOf(PropTypes.shape({
    field: PropTypes.string.isRequired,
    reason: PropTypes.string.isRequired,
    observationIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    overrideId: PropTypes.string,
  })).isRequired,
};

/**
 * Side-by-side live vs proposed shadow profile for one beer.
 */
function BeerProfileShadowComparisonPanel({ beerId, beerName }) {
  const { version, loading, error, refresh } = useLatestBeerProfileVersion(beerId);
  const [recompareLoading, setRecompareLoading] = useState(false);
  const [recompareError, setRecompareError] = useState(null);
  const [recompareStatus, setRecompareStatus] = useState(null);

  const handleRecompare = useCallback(async () => {
    const trimmedBeerId = typeof beerId === 'string' ? beerId.trim() : '';
    if (!trimmedBeerId) {
      return;
    }
    setRecompareLoading(true);
    setRecompareError(null);
    setRecompareStatus(null);
    try {
      const callable = httpsCallable(
        functions,
        CALLABLE_BEER_RECOMPARE_BEER_PROFILE,
      );
      const response = await callable({ beerId: trimmedBeerId });
      const payload = response?.data ?? {};
      setRecompareStatus(
        typeof payload.status === 'string' ? payload.status : 'completed',
      );
      await refresh();
    } catch (callableError) {
      console.error('recompareBeerProfile failed', callableError);
      setRecompareError(
        callableError?.message
          || 'Failed to recompare shadow profile',
      );
    } finally {
      setRecompareLoading(false);
    }
  }, [beerId, refresh]);

  const showInitialSpinner = loading && !version;
  if (showInitialSpinner) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const comparison = version?.comparison ?? null;
  const fieldSources = version?.fieldSources ?? {};
  const conflicts = normalizeBeerProfileConflicts(version?.conflicts);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" component="h2">
          Shadow profile comparison
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {beerName || beerId} — operator-only; does not change the live beer.
        </Typography>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {recompareError ? <Alert severity="error">{recompareError}</Alert> : null}
      {recompareStatus && !error ? (
        <Alert severity="success">
          Recompare finished ({recompareStatus}).
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleRecompare}
          disabled={recompareLoading || loading || !beerId}
        >
          {recompareLoading ? 'Recomparing…' : 'Recompare'}
        </Button>
        <Button variant="text" size="small" onClick={refresh} disabled={loading}>
          Refresh
        </Button>
        {loading ? <CircularProgress size={18} aria-label="Refreshing shadow profile" /> : null}
      </Box>

      {!version && !error ? (
        <Alert severity="info">
          No shadow profile version yet for this beer. Apply an ingestion review
          with shadow rebuild enabled, or run Recompare after observations exist.
        </Alert>
      ) : null}

      {version && !comparison ? (
        <Alert severity="warning">
          Latest version has no comparison yet (pre-ITT-970). Run Recompare to
          attach outcomes without rewriting profile or provenance.
        </Alert>
      ) : null}

      {version ? (
        <Typography variant="caption" color="text.secondary" component="p">
          Version {version.id} · {version.resolverVersion} · hash {version.inputHash}
        </Typography>
      ) : null}

      {comparison ? (
        <Box>
          <Box
            sx={{
              display: { xs: 'none', sm: 'grid' },
              gridTemplateColumns: '120px 1fr 1fr 1fr',
              gap: 1,
              pb: 0.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">Field</Typography>
            <Typography variant="caption" color="text.secondary">Live value</Typography>
            <Typography variant="caption" color="text.secondary">Proposed value</Typography>
            <Typography variant="caption" color="text.secondary">Evidence</Typography>
          </Box>
          {BEER_PROFILE_COMPARISON_FIELDS.map((field) => (
            <ComparisonFieldRow
              key={field}
              field={field}
              fieldEntry={comparison.fields?.[field]}
              fieldSource={fieldSources[field]}
            />
          ))}
        </Box>
      ) : null}

      {version ? (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Conflicts
          </Typography>
          <ConflictsList conflicts={conflicts} />
        </Box>
      ) : null}
    </Stack>
  );
}

BeerProfileShadowComparisonPanel.propTypes = {
  beerId: PropTypes.string,
  beerName: PropTypes.string,
};

BeerProfileShadowComparisonPanel.defaultProps = {
  beerId: null,
  beerName: null,
};

export default BeerProfileShadowComparisonPanel;
